import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'

const DEFAULT_OG_IMAGE = '/og.png'

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage = DEFAULT_OG_IMAGE,
    imageAlt,
    path = '',
    type = 'website',
    publishedTime,
    modifiedTime,
    section,
    tags,
    authorName,
    noindex = false
}): ReactNode => {
    const pageTitle = title
        ? `${title} | ${siteConfig.name}`
        : `${siteConfig.name} | ${siteConfig.tagline ?? ''}`.trim()

    const url = `${siteConfig.url}${path}`
    const image = ogImage.startsWith('http') ? ogImage : `${siteConfig.url}${ogImage}`
    const imageAltText = imageAlt ?? `${siteConfig.name} | ${siteConfig.tagline ?? 'Evidence-based dental knowledge'}`
    const isDefaultImage = ogImage === DEFAULT_OG_IMAGE

    const robots = noindex
        ? 'noindex, follow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <meta name='robots' content={robots} />
            <link rel='canonical' href={url} />

            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content={type} />
            <meta property='og:url' content={url} />
            <meta property='og:locale' content='en_US' />
            <meta property='og:image' content={image} />
            <meta property='og:image:alt' content={imageAltText} />

            {isDefaultImage && (
                <>
                    <meta property='og:image:width' content='1344' />
                    <meta property='og:image:height' content='768' />
                    <meta property='og:image:type' content='image/png' />
                </>
            )}

            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />
            <meta name='twitter:image' content={image} />
            <meta name='twitter:image:alt' content={imageAltText} />

            {type === 'article' && (
                <>
                    {publishedTime && (
                        <meta property='article:published_time' content={publishedTime} />
                    )}
                    {modifiedTime && (
                        <meta property='article:modified_time' content={modifiedTime} />
                    )}
                    {section && <meta property='article:section' content={section} />}
                    {authorName && <meta property='article:author' content={authorName} />}
                    {tags?.map(tag => (
                        <meta key={tag} property='article:tag' content={tag} />
                    ))}
                </>
            )}
        </Head>
    )
}
